import React, { useState, useRef } from 'react';
import { Check, Building2, Users, ArrowRight, ChevronRight, Upload, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../App';
import { ORG_TYPES } from '../lib/supabase';
import './OnboardingPage.css';

const STEPS = ['Welcome', 'Your Organization', 'Invite Team', 'All Set'];

const OnboardingPage = ({ currentUser, onOrgCreated }) => {
  const [step, setStep] = useState(0);

  // Org creation state
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState('');
  const [createdOrg, setCreatedOrg] = useState(null);
  const [finishing, setFinishing] = useState(false);

  // Invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // CSV bulk invite state
  const csvInputRef = useRef(null);
  const [csvRows, setCsvRows] = useState([]);
  const [csvSending, setCsvSending] = useState(false);

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!orgName.trim() || !orgType) return;
    setOrgLoading(true);
    setOrgError('');
    try {
      const res = await axios.post(`${API_URL}/api/org/create`, { name: orgName.trim(), type: orgType });
      setCreatedOrg(res.data.org);
      setStep(2);
    } catch (err) {
      setOrgError(err.response?.data?.error || 'Failed to create organization. Please try again.');
    } finally {
      setOrgLoading(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const res = await axios.post(`${API_URL}/api/org/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInvites((prev) => [...prev, { email: inviteEmail.trim(), role: inviteRole, token: res.data.token }]);
      setInviteEmail('');
      setInviteSuccess(`Invite sent to ${inviteEmail.trim()}`);
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invite.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').map(l => l.trim()).filter(Boolean);
      const parsed = [];
      for (const line of lines) {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        const email = cols[0];
        if (!email || email.toLowerCase() === 'email') continue;
        const role = ['admin', 'member', 'viewer'].includes((cols[1] || '').toLowerCase())
          ? cols[1].toLowerCase() : 'member';
        parsed.push({ email, role, status: null, error: null });
      }
      setCsvRows(parsed);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSendAllInvites = async () => {
    if (!csvRows.length || csvSending) return;
    setCsvSending(true);
    const updated = [...csvRows];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'done') continue;
      updated[i] = { ...updated[i], status: 'sending' };
      setCsvRows([...updated]);
      try {
        const res = await axios.post(`${API_URL}/api/org/invite`, { email: updated[i].email, role: updated[i].role });
        updated[i] = { ...updated[i], status: 'done', token: res.data.token };
        setInvites(prev => [...prev, { email: updated[i].email, role: updated[i].role, token: res.data.token }]);
      } catch (err) {
        updated[i] = { ...updated[i], status: 'error', error: err.response?.data?.error || 'Failed' };
      }
      setCsvRows([...updated]);
    }
    setCsvSending(false);
  };

  const handleFinish = async () => {
    setFinishing(true);
    // Best-effort: refresh the app's user so it picks up the new org. Navigate
    // regardless — a hard load re-initialises with the persisted org and lands on
    // the dashboard, so the button never dead-ends even if the refresh is slow.
    try {
      if (onOrgCreated && createdOrg) {
        await onOrgCreated(createdOrg);
      }
    } catch (err) {
      console.error('[onboarding] finish refresh failed:', err);
    }
    window.location.assign('/home');
  };

  return (
    <div className="onboarding-container">
      {/* Progress bar */}
      <div className="onboarding-progress">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className={`progress-step ${i <= step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <div className="progress-dot">
                {i < step ? <Check size={12} /> : <span>{i + 1}</span>}
              </div>
              <span className="progress-label">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`progress-line ${i < step ? 'done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="onboarding-card">
        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="onboarding-step">
            <div className="onboarding-icon">
              <Building2 size={40} />
            </div>
            <h1>Welcome to DealNexa{currentUser?.name ? `, ${currentUser.name.split(' ')[0]}` : ''}!</h1>
            <p className="onboarding-subtitle">
              Let's get your workspace set up. It only takes a minute to configure your organization
              and invite your team.
            </p>
            <div className="onboarding-checklist">
              <div className="checklist-item">
                <ChevronRight size={16} />
                <span>Create your organization</span>
              </div>
              <div className="checklist-item">
                <ChevronRight size={16} />
                <span>Invite your teammates</span>
              </div>
              <div className="checklist-item">
                <ChevronRight size={16} />
                <span>Start analyzing deals</span>
              </div>
            </div>
            <button className="btn-primary-onboarding" onClick={() => setStep(1)}>
              Get Started <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Step 1: Create Org */}
        {step === 1 && (
          <div className="onboarding-step">
            <h2>Set up your organization</h2>
            <p className="onboarding-subtitle">This will be your team's workspace for all deals and due diligence.</p>

            {orgError && <div className="onboarding-error">{orgError}</div>}

            <form onSubmit={handleCreateOrg} className="onboarding-form">
              <div className="form-field">
                <label>Organization Name</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. Acme Capital Partners"
                  required
                  autoFocus
                />
              </div>

              <div className="form-field">
                <label>Organization Type</label>
                <div className="org-type-grid">
                  {ORG_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className={`org-type-card ${orgType === t.value ? 'selected' : ''}`}
                      onClick={() => setOrgType(t.value)}
                    >
                      <span className="org-type-label">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="onboarding-actions">
                <button type="button" className="btn-secondary-onboarding" onClick={() => setStep(0)}>
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary-onboarding"
                  disabled={!orgName.trim() || !orgType || orgLoading}
                >
                  {orgLoading ? 'Creating...' : 'Create Organization'}
                  {!orgLoading && <ArrowRight size={16} />}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Invite Team */}
        {step === 2 && (
          <div className="onboarding-step">
            <div className="onboarding-icon invite-icon">
              <Users size={40} />
            </div>
            <h2>Invite your team</h2>
            <p className="onboarding-subtitle">
              Add colleagues to <strong>{createdOrg?.name}</strong>. They'll receive a link to join.
            </p>

            {inviteError && <div className="onboarding-error">{inviteError}</div>}
            {inviteSuccess && <div className="onboarding-success">{inviteSuccess}</div>}

            <form onSubmit={handleSendInvite} className="invite-form">
              <div className="invite-row">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="invite-email-input"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="invite-role-select"
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button
                  type="submit"
                  className="btn-invite"
                  disabled={!inviteEmail.trim() || inviteLoading}
                >
                  {inviteLoading ? '...' : 'Invite'}
                </button>
              </div>
            </form>

            {/* CSV bulk invite */}
            <div className="onboarding-csv-section">
              <div className="onboarding-csv-divider"><span>or import from CSV</span></div>
              <div className="onboarding-csv-hint">Format: <code>email, role</code> — role optional, defaults to member</div>
              <button className="onboarding-csv-btn" onClick={() => csvInputRef.current?.click()}>
                <Upload size={14} /> Upload CSV
              </button>
              <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvUpload} />

              {csvRows.length > 0 && (
                <div className="onboarding-csv-preview">
                  <table className="csv-table">
                    <thead>
                      <tr><th>Email</th><th>Role</th><th>Status</th><th></th></tr>
                    </thead>
                    <tbody>
                      {csvRows.map((row, i) => (
                        <tr key={i}>
                          <td>{row.email}</td>
                          <td>
                            <select
                              value={row.role}
                              disabled={csvSending || row.status === 'done'}
                              onChange={e => setCsvRows(prev => prev.map((r, j) => j === i ? { ...r, role: e.target.value } : r))}
                              className="csv-role-select"
                            >
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                              <option value="viewer">Viewer</option>
                            </select>
                          </td>
                          <td>
                            {row.status === null && <span className="csv-status-pending">Pending</span>}
                            {row.status === 'sending' && <span className="csv-status-sending">Sending…</span>}
                            {row.status === 'done' && <span className="csv-status-done"><Check size={13} /> Sent</span>}
                            {row.status === 'error' && <span className="csv-status-error"><AlertCircle size={13} /> {row.error}</span>}
                          </td>
                          <td>
                            <button className="csv-remove-btn" disabled={csvSending || row.status === 'done'}
                              onClick={() => setCsvRows(prev => prev.filter((_, j) => j !== i))}>
                              <X size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="csv-actions">
                    <span className="csv-count">{csvRows.length} recipient{csvRows.length !== 1 ? 's' : ''}</span>
                    <button
                      className="btn-invite"
                      disabled={csvSending || csvRows.every(r => r.status === 'done')}
                      onClick={handleSendAllInvites}
                    >
                      {csvSending ? 'Sending…' : 'Send All Invites'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {invites.length > 0 && (
              <div className="invited-list">
                <p className="invited-label">Invited ({invites.length})</p>
                {invites.map((inv, i) => (
                  <div key={i} className="invited-item">
                    <div className="invited-avatar">{inv.email[0].toUpperCase()}</div>
                    <span className="invited-email">{inv.email}</span>
                    <span className="invited-role-badge">{inv.role}</span>
                    <Check size={14} className="invited-check" />
                  </div>
                ))}
              </div>
            )}

            <div className="onboarding-actions">
              <button
                type="button"
                className="btn-secondary-onboarding"
                onClick={() => setStep(3)}
              >
                Skip for now
              </button>
              <button
                type="button"
                className="btn-primary-onboarding"
                onClick={() => setStep(3)}
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: All Set */}
        {step === 3 && (
          <div className="onboarding-step onboarding-done">
            <div className="done-icon">
              <Check size={48} />
            </div>
            <h2>You're all set!</h2>
            <p className="onboarding-subtitle">
              {createdOrg
                ? `${createdOrg.name} is ready to go. Start by uploading deal documents or creating your first deal.`
                : 'Your workspace is ready. Start by uploading deal documents or creating your first deal.'}
            </p>
            <div className="done-actions">
              <button className="btn-primary-onboarding large" onClick={handleFinish} disabled={finishing}>
                {finishing ? 'Setting up…' : <>Go to Dashboard <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
